// The check character on friend codes and access codes (#6984), and the two
// alphabets it is computed over.
//
// A check character is calculated from the other characters, the way the last
// digit of a card number is, so one mistyped character makes the check fail and
// the app can say "check it for typos" with certainty, without looking anything
// up. It is added only when a code is SHOWN: stored codes, the keys database and
// invite links keep their old form, and a code typed without it still works.
// See design-docs/subscribe-code-field.md, "The check character".

// The alphabet a person can read aloud or type without getting it wrong.
//
// Four characters are left out, and they are the two pairs that get misread:
// `0` against `O`, and `1` against `I`. Removing one of each pair is enough, so
// the digits `0` and `1` go and the letters `O` and `I` go with them — which
// leaves the 8 digits `2`-`9` and the 24 letters `A`-`Z` without `I` or `O`.
//
// That is EXACTLY 32 characters, and the exactness is load-bearing rather than
// tidy: 32 is 2^5, so each character carries exactly 5 bits and a code can be
// generated by slicing random bytes, with no rejection sampling and no bias. Add
// or remove one and `generateReferralCode` silently starts favouring the early
// characters. `L` is deliberately KEPT: it is only confusable with `1`, which is
// not in the set, so there is nothing left for it to be mistaken for.
//
// Never change this without re-minting every code that already exists — an old
// code holding a character the new alphabet rejects stops being claimable.
// (It lives here rather than in referral.ts so referral.ts can use the check
// character without the two files importing each other; referral.ts re-exports it.)
export const REFERRAL_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** How many characters a referral code has. Short enough to read out, long enough not to collide. */
export const REFERRAL_CODE_LENGTH = 8;

/**
 * The access-key alphabet: the `base32` library's alphabet, upper-cased. Access
 * keys are minted with that library (`generateDigestForActivationKey`), which
 * stores them in lower case; `normaliseAccessKeyText` brings typed text into
 * this form.
 */
export const ACCESS_KEY_ALPHABET = '0123456789ABCDEFGHJKMNPQRTUVWXYZ';

/** How many characters an access key has, without its check character. */
export const ACCESS_KEY_LENGTH = 16;

/** The letters the `base32` library reads as digits, because people type them for one another. */
const ACCESS_KEY_ALIASES: Readonly<Record<string, string>> = { O: '0', I: '1', L: '1', S: '5' };

/**
 * Upper-cases typed access-key text and maps the letters a person types for a digit
 * (`O`→`0`, `I`→`1`, `L`→`1`, `S`→`5`), as the `base32` library does when it decodes.
 */
export function normaliseAccessKeyText(text: string): string {
  return text.toUpperCase().replace(/[OILS]/g, ch => ACCESS_KEY_ALIASES[ch]);
}

/**
 * The Luhn mod N check character of `body` over `alphabet` (N = 32 here): the
 * card-number method generalised to a 32-character alphabet. It catches every
 * single-character substitution and most swaps of two neighbours. Answers
 * undefined when `body` holds a character the alphabet does not.
 */
export function checkCharacter(body: string, alphabet: string): string | undefined {
  const n = alphabet.length;
  let sum = 0;
  // double every second character, starting with the right-most
  let factor = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    const value = alphabet.indexOf(body[i]);
    if (value < 0) return undefined;
    const doubled = value * factor;
    sum += Math.floor(doubled / n) + (doubled % n);
    factor = factor === 2 ? 1 : 2;
  }
  return alphabet[(n - (sum % n)) % n];
}

/**
 * The first `bodyLength` characters of `code` when its one remaining character is their
 * correct check character, else undefined (another length, or a mistyped code). The one
 * place the device and the server both verify and drop a check character.
 */
export function withoutCheckCharacter(code: string, alphabet: string, bodyLength: number): string | undefined {
  if (code.length !== bodyLength + 1) return undefined;
  const body = code.slice(0, bodyLength);
  return checkCharacter(body, alphabet) === code[bodyLength] ? body : undefined;
}

/** A stored 8-character friend code with its check character appended (9 characters). */
export function withFriendCheck(code: string): string {
  const body = code.toUpperCase();
  const check = body.length === REFERRAL_CODE_LENGTH ? checkCharacter(body, REFERRAL_CODE_ALPHABET) : undefined;
  if (!check) throw new Error(`withFriendCheck: not ${REFERRAL_CODE_LENGTH} characters of the friend-code alphabet (length ${code.length})`);
  return body + check;
}

/**
 * A 16-character access key with its check character appended (17 characters).
 * Accepts the key as the database stores it, in lower case, or typed with aliases.
 */
export function withAccessCheck(key: string): string {
  const body = normaliseAccessKeyText(key);
  const check = body.length === ACCESS_KEY_LENGTH ? checkCharacter(body, ACCESS_KEY_ALPHABET) : undefined;
  if (!check) throw new Error(`withAccessCheck: not ${ACCESS_KEY_LENGTH} characters of the access-key alphabet (length ${key.length})`);
  return body + check;
}

/** A 17-character access key as people see it: `XXXX-XXXX-XXXX-XXXXX`. */
export function formatAccessKey(key17: string): string {
  return [key17.slice(0, 4), key17.slice(4, 8), key17.slice(8, 12), key17.slice(12)].join('-');
}
